import React from 'react';
import { Box, LinearProgress, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import {
  GetInterventionLengthsResponse,
  InterventionLadderStepModel,
  InterventionLengthCourses,
  InterventionLengthParagraphModel,
  InterventionLengthUnit,
  InterventionLengthYearModel,
} from '../../../api-service/clients';
import { CircularProgressCentered } from '../../../components/circular-progress-centered';
import { formatNumber } from '../../../utilities';
import { stepBackgrounds, stepColor, stepMarkerBorders } from '../intervention-ladder/intervention-ladder-colors';
import { InterventionLengthChart } from './intervention-length-chart';

const selectedColor = stepBackgrounds[stepBackgrounds.length - 1];

const coursesOptions = [
  { courses: InterventionLengthCourses.Active, label: 'Aktive', caption: 'igangværende indsatser 31/12 og i dag', empty: 'Ingen igangværende indsatser' },
  { courses: InterventionLengthCourses.Ended, label: 'Afsluttede', caption: 'indsatser afsluttet i året, med hele længden', empty: 'Ingen afsluttede indsatser' },
];

export interface InterventionLengthsComponentProps {
  step: InterventionLadderStepModel;
  intensity: number;
  courses: InterventionLengthCourses;
  lengths: GetInterventionLengthsResponse | undefined;
  isLoading: boolean;
  hasFailed: boolean;
  onSelectCourses: (courses: InterventionLengthCourses) => void;
}

export function InterventionLengthsComponent({ step, intensity, courses, lengths, isLoading, hasFailed, onSelectCourses }: InterventionLengthsComponentProps) {
  const option = coursesOptions.find(candidate => candidate.courses === courses) ?? coursesOptions[0];
  return (
    <Paper sx={{ p: 2, mt: 2, position: 'relative', overflow: 'hidden' }} aria-busy={isLoading}>
      {isLoading && lengths !== undefined && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3 }} />}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box>
          <Typography variant="h2" sx={{ fontSize: '1rem', mb: 0.5 }}>
            Indsatslængde
          </Typography>
          <Typography sx={{ fontSize: '0.8125rem', fontStyle: 'italic', color: 'text.secondary' }}>
            <Box component="strong" sx={{ fontStyle: 'normal', fontWeight: 600, color: 'text.primary' }}>
              Trin {step.step} · {step.title}
            </Box>{' '}
            · {option.caption}
          </Typography>
        </Box>
        <ToggleButtonGroup exclusive size="small" value={courses} aria-label="Indsatser" onChange={(_, value: InterventionLengthCourses | null) => value && onSelectCourses(value)}>
          {coursesOptions.map(candidate => (
            <ToggleButton
              key={candidate.courses}
              value={candidate.courses}
              sx={{ textTransform: 'none', fontWeight: 600, py: 0.25, '&.Mui-selected, &.Mui-selected:hover': { bgcolor: selectedColor, color: '#ffffff' } }}
            >
              {candidate.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>
      <InterventionLengthsParagraphs lengths={lengths} isLoading={isLoading} hasFailed={hasFailed} intensity={intensity} emptyText={option.empty} />
    </Paper>
  );
}

interface InterventionLengthsParagraphsProps {
  lengths: GetInterventionLengthsResponse | undefined;
  isLoading: boolean;
  hasFailed: boolean;
  intensity: number;
  emptyText: string;
}

function InterventionLengthsParagraphs({ lengths, isLoading, hasFailed, intensity, emptyText }: InterventionLengthsParagraphsProps) {
  if (hasFailed) {
    return <Typography>Indsatslængderne kunne ikke hentes.</Typography>;
  }
  if (lengths === undefined) {
    return <CircularProgressCentered />;
  }
  if (lengths.unit === undefined || lengths.paragraphs.length === 0) {
    return <Typography>Indsatslængderne er ikke sat op for trinnet.</Typography>;
  }
  const { unit } = lengths;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(480px, 1fr))', gap: 2.5, opacity: isLoading ? 0.5 : 1, transition: 'opacity 150ms' }}>
      {lengths.paragraphs.map(paragraph => (
        <InterventionLengthParagraph key={paragraph.paragraph + paragraph.name} paragraph={paragraph} unit={unit} intensity={intensity} emptyText={emptyText} />
      ))}
    </Box>
  );
}

interface InterventionLengthParagraphProps {
  paragraph: InterventionLengthParagraphModel;
  unit: InterventionLengthUnit;
  intensity: number;
  emptyText: string;
}

function InterventionLengthParagraph({ paragraph, unit, intensity, emptyText }: InterventionLengthParagraphProps) {
  const thisYear = paragraph.years[paragraph.years.length - 1];
  const thisYearsCitizens = thisYear === undefined ? 0 : citizensOf(thisYear);
  const hasCitizens = paragraph.years.some(year => citizensOf(year) > 0);
  const barColor = stepColor(stepMarkerBorders, intensity);

  return (
    <Box
      component="section"
      aria-label={`${paragraph.paragraph} ${paragraph.name}`}
      sx={{ borderRadius: '10px', borderTop: `4px solid ${barColor}`, boxShadow: '0 2px 6px rgba(24, 76, 97, 0.12)', bgcolor: 'background.paper', px: 2, pt: 1.75, pb: 1.5 }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              px: 1,
              py: 0.25,
              borderRadius: '6px',
              fontSize: '0.8125rem',
              fontWeight: 700,
              bgcolor: stepColor(stepBackgrounds, intensity),
              color: intensity >= 0.5 ? '#ffffff' : '#16323d',
            }}
          >
            {paragraph.paragraph}
          </Box>
          <Typography
            component="h3"
            title={paragraph.name}
            sx={{ mt: 0.75, fontSize: '0.9375rem', fontWeight: 500, color: '#16323d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {paragraph.name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2.5, flex: 'none', textAlign: 'right' }}>
          <ParagraphNumber value={formatNumber(thisYearsCitizens)} label={thisYear === undefined ? 'borgere' : `borgere ${thisYear.year}`} />
          <ParagraphNumber value={averageDaysText(thisYear?.averageDays)} label={averageYearsText(thisYear?.averageDays)} />
        </Box>
      </Box>
      {hasCitizens ? (
        <InterventionLengthChart
          labels={(thisYear?.intervals ?? []).map(interval => intervalLabel(interval.from, interval.to, unit))}
          series={paragraph.years.map((year, index) => ({
            label: `${year.year}`,
            citizens: year.intervals.map(interval => interval.citizens),
            barColor: yearColor(barColor, index, paragraph.years.length),
            hoverColor: stepColor(stepMarkerBorders, Math.min(1, intensity + 0.25)),
          }))}
        />
      ) : (
        <Box sx={{ height: 300, display: 'grid', placeItems: 'center', color: 'text.secondary', fontSize: '0.875rem' }}>{emptyText}</Box>
      )}
    </Box>
  );
}

interface ParagraphNumberProps {
  value: string;
  label: string;
}

function ParagraphNumber({ value, label }: ParagraphNumberProps) {
  return (
    <Box sx={{ whiteSpace: 'nowrap' }}>
      <Box sx={{ fontSize: '1.25rem', fontWeight: 500, lineHeight: 1.1, color: '#16323d', fontVariantNumeric: 'tabular-nums' }}>{value}</Box>
      <Box sx={{ fontSize: '0.6875rem', color: 'text.secondary', mt: 0.25 }}>{label}</Box>
    </Box>
  );
}

function citizensOf(year: InterventionLengthYearModel) {
  return year.intervals.reduce((sum, interval) => sum + interval.citizens, 0);
}

// Earlier years fade towards white, so this year stands out in the step's own color.
export function yearColor(barColor: string, index: number, yearCount: number) {
  const strength = Math.round(100 - ((yearCount - 1 - index) * 60) / Math.max(1, yearCount - 1));
  return strength === 100 ? barColor : `color-mix(in oklch, ${barColor} ${strength}%, #ffffff)`;
}

const monthsPerYear = 12;
// The same year as the ladder's person-years.
const daysPerYear = 365;

// The BFF sends null where nothing is counted, although the generated types only say undefined.
export function averageDaysText(averageDays: number | null | undefined) {
  return averageDays === undefined || averageDays === null ? '–' : `${formatNumber(Math.round(averageDays))} døgn`;
}

export function averageYearsText(averageDays: number | null | undefined) {
  return averageDays === undefined || averageDays === null ? 'gns. længde' : `gns. · ${formatNumber(averageDays / daysPerYear, 1)} år`;
}

export function intervalLabel(from: number, to: number | null | undefined, unit: InterventionLengthUnit) {
  const unitText = unit === InterventionLengthUnit.Months ? 'mdr.' : 'år';
  if (to !== undefined && to !== null) {
    return `${from}–${to} ${unitText}`;
  }
  if (unit === InterventionLengthUnit.Months && from > 0 && from % monthsPerYear === 0) {
    return `${from / monthsPerYear}+ år`;
  }
  return `${from}+ ${unitText}`;
}
